import React, {useMemo} from 'react'
import {
    Flex,
    Box,
    Text,
    useMultiStyleConfig,
    Divider,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import RecipeUtensils from './recipe-utensils'
import {FormattedMessage} from 'react-intl'

const IngredientsMenu = ({
    ingredients,
    servings,
    allUtensils = [],
    allIngredients = [],
    allUnits = [],
}) => {
    const styles = useMultiStyleConfig('RecipeDetails', {})
    const {ingredientsMenu} = styles || {}

    // Split into grouped vs main ingredients
    const {groupedIngredients, mainIngredients} = useMemo(() => {
        if (!ingredients?.length) return {groupedIngredients: [], mainIngredients: []}

        const grouped = []
        const main = []

        for (let ing of ingredients) {
            if (ing?.ingredient_group) {
                grouped.push(ing)
            } else {
                main.push(ing)
            }
        }

        return {groupedIngredients: grouped, mainIngredients: main}
    }, [ingredients])

    return (
        <Box {...ingredientsMenu?.outerContainer}>
            <Box {...ingredientsMenu?.stickyContainer}>
                <Text as="h2" {...ingredientsMenu?.ingredientsTitle}>
                    <FormattedMessage
                        id="recipes.details_page.ingredients.title"
                        defaultMessage="Ingredients"
                    />
                </Text>

                <Flex>
                    {servings?.servings && (
                        <Flex {...ingredientsMenu?.servingsContainer}>
                            <Text {...ingredientsMenu?.ingredientMenuTitle}>
                                {' '}
                                <FormattedMessage
                                    id="recipes.details_page.servings.title"
                                    defaultMessage="Servings"
                                />
                            </Text>
                            <Text {...ingredientsMenu?.servingsNumber}>
                                {servings.servings.toString()}
                            </Text>
                        </Flex>
                    )}
                    {servings?.makes && (
                        <Flex {...ingredientsMenu?.servingsContainer}>
                            <Text {...ingredientsMenu?.ingredientMenuTitle}>
                                {' '}
                                <FormattedMessage
                                    id="recipe.details_page.servings.makes_text"
                                    defaultMessage="Makes"
                                />
                            </Text>
                            <Text {...ingredientsMenu?.servingsNumber}>
                                {servings.makes.toString()}
                            </Text>
                        </Flex>
                    )}
                </Flex>

                {/* Grouped Ingredients */}
                {groupedIngredients.map((ing, idx) => (
                    <Flex
                        data-testid={`grouped-ingredient=${idx}`}
                        key={`group-${idx}`}
                        {...ingredientsMenu?.ingredientContainer}
                    >
                        <Text {...ingredientsMenu?.ingredientMenuTitle}>
                            {ing?.ingredient_group}
                        </Text>
                        <ul>
                            {ing?.ingredient?.map((mainIng, subIdx) => {
                                const ingredient = allIngredients.find(
                                    (obj) => obj.uid === mainIng?.mainIngredient?.ingredientId,
                                )
                                const unit = allUnits.find(
                                    (u) => u.uid === mainIng?.mainIngredient?.unitId,
                                )
                                return (
                                    <li key={`group-${ing?._metadata?.uid || idx}-${subIdx}`}>
                                        <Text {...ingredientsMenu?.ingredientText}>
                                            {`${mainIng?.mainIngredient?.quantity || ''} ${
                                                unit?.title || ''
                                            } ${ingredient?.title || ''}${
                                                mainIng?.mainIngredient?.notes
                                                    ? `, ${mainIng.mainIngredient.notes}`
                                                    : ''
                                            }`}
                                        </Text>
                                    </li>
                                )
                            })}
                        </ul>
                    </Flex>
                ))}

                {/* Main Ingredients */}
                {mainIngredients.map((ing, idx) => (
                    <Flex
                        data-testid={`main-ingredient=${idx}`}
                        key={`main-${idx}`}
                        {...ingredientsMenu?.ingredientContainer}
                    >
                        {ing?.ingredient?.map((mainIng, subIdx) => {
                            const ingredient = allIngredients.find(
                                (obj) => obj.uid === mainIng?.mainIngredient?.ingredientId,
                            )
                            const unit = allUnits.find(
                                (u) => u.uid === mainIng?.mainIngredient?.unitId,
                            )
                            return (
                                <Text
                                    key={`main-${ing?._metadata?.uid || idx}-${subIdx}`}
                                    {...ingredientsMenu?.ingredientText}
                                >
                                    {`${mainIng?.mainIngredient?.quantity || ''} ${
                                        unit?.title || ''
                                    } ${ingredient?.title || ''}${
                                        mainIng?.mainIngredient?.notes
                                            ? `, ${mainIng.mainIngredient.notes}`
                                            : ''
                                    }`}
                                </Text>
                            )
                        })}
                    </Flex>
                ))}

                <Divider />

                {Object.keys(allUtensils).length > 0 && (
                    <RecipeUtensils allUtensils={allUtensils} />
                )}
            </Box>
        </Box>
    )
}

IngredientsMenu.propTypes = {
    ingredients: PropTypes.array,
    servings: PropTypes.object,
    allUtensils: PropTypes.array,
    allIngredients: PropTypes.array,
    allUnits: PropTypes.array,
}

export default IngredientsMenu
